import React, { useCallback, useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useUser } from "@hooks/useUser";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { Button } from "@design-system/Button/Button";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { FinancialAccountsConnectionProvider } from "./FinancialAccountsConnectionProvider";
import { isValidIntegrationConnection } from "./helpers";
import { FinancialAccountsConnectionWidgetV2 } from "./FinancialAccountConnectionWidgetV2";

interface FinancialAccountsConnectedModalProps {
    show: boolean;
    integrationConnection: IntegrationConnection;
    onHide(): void;
}

export const FinancialAccountsConnectedModalV2: React.FC<
    FinancialAccountsConnectedModalProps
> = ({ show, integrationConnection, onHide }) => {
    const { showBusinessEntitySelection } =
        useFinancialAccountEntitySelection();
    const [submitted, setSubmitted] = useState<boolean>(false);
    const user = useUser();
    const handleSubmit = useCallback(() => {
        if (
            isValidIntegrationConnection(integrationConnection) ||
            submitted ||
            !user.onboardingComplete
        ) {
            setSubmitted(false);
            onHide();
        } else {
            setSubmitted(true);
        }
    }, [integrationConnection, onHide, submitted, user.onboardingComplete]);

    return (
        <FinancialAccountsConnectionProvider
            showAccountTypeWarning={submitted && user.onboardingComplete}
        >
            <StandardModal
                show={show}
                onHide={onHide}
                size={
                    integrationConnection && showBusinessEntitySelection
                        ? "xl"
                        : "lg"
                }
                backdrop={integrationConnection ? true : "static"}
                dialogClassName={
                    showBusinessEntitySelection
                        ? "accounts-connected-modal__dialog--with-entities"
                        : "accounts-connected-modal__dialog"
                }
            >
                <>
                    <StandardModalHeader>Select entity</StandardModalHeader>
                    <StandardModalBody>
                        <p>For which entity are you adding these accounts?</p>

                        <FinancialAccountsConnectionWidgetV2
                            accounts={
                                integrationConnection.integrationAccounts ?? []
                            }
                            integrationConnection={integrationConnection}
                        />

                        <HStack as="footer" align="center" justify="end">
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                size="xl"
                            >
                                Continue
                            </Button>
                        </HStack>
                    </StandardModalBody>
                </>
            </StandardModal>
        </FinancialAccountsConnectionProvider>
    );
};
